"use client"

import { HeroBannerButtonStyles, HeroBannerContentProps, HeroBannerSlide } from "@/types/types"
import AOS from "aos"
import "aos/dist/aos.css"
import { useEffect } from "react"
import "swiper/css"
import "swiper/css/navigation"
import "swiper/css/pagination"
import { Autoplay, Navigation, Pagination } from "swiper/modules"
import { Swiper, SwiperSlide } from "swiper/react"
import { twMerge } from "tailwind-merge"

import { HeroBannerSlideContent } from "./HeroBannerSlideContent"

export const HeroBannerContent = ({
  id,
  slides,
  swiperConfig,
  background,
  backgroundImage, // Legacy support
  topText,
  mainText,
  mainTextStyles,
  listButtons,
  buttonStyles,
  className,
  // Legacy props for backward compatibility
  mainTextFontSize,
  mainTextFontWeight,
  mainTextLineHeight,
  backgroundHeight,
  buttonSize,
  buttonFontSize,
  buttonFontWeight,
  buttonLineHeight,
  buttonGap,
}: HeroBannerContentProps) => {
  useEffect(() => {
    if (typeof window !== "undefined") {
      const isAOSInitialized = (window as any).AOS?.initialized
      if (!isAOSInitialized) {
        AOS.init({
          duration: 1000,
          once: false,
        })
      } else {
        AOS.refresh()
      }
      setTimeout(() => {
        AOS.refresh()
      }, 100)
    }
  }, [slides, background?.image, backgroundImage, topText, mainText, listButtons])

  // Determine if we should use swiper mode
  // Use swiper if explicitly enabled AND has slides (even if only 1 slide)
  const useSwiper = swiperConfig?.enabled === true && slides && slides.length > 0

  // If swiper mode, render with Swiper
  if (useSwiper) {
    const swiperModules = []
    if (swiperConfig.navigation) swiperModules.push(Navigation)
    if (swiperConfig.pagination) swiperModules.push(Pagination)
    if (swiperConfig.autoplay) swiperModules.push(Autoplay)

    return (
      <div id={id} className={twMerge("relative w-full", className)}>
        <Swiper
          modules={swiperModules}
          spaceBetween={swiperConfig.spaceBetween || 0}
          speed={swiperConfig.speed || 600}
          loop={swiperConfig.loop || false}
          autoplay={
            swiperConfig.autoplay
              ? {
                  delay: swiperConfig.autoplayDelay || 3000,
                  disableOnInteraction: false,
                }
              : false
          }
          navigation={swiperConfig.navigation || false}
          pagination={swiperConfig.pagination ? { clickable: true } : false}
          className="w-full"
        >
          {slides.map((slide, index) => (
            <SwiperSlide key={index}>
              <HeroBannerSlideContent slide={slide} buttonStyles={buttonStyles} />
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    )
  }

  // Single slide mode (backward compatibility)
  // Convert single slide props to slide object
  const singleSlide: HeroBannerSlide = {
    background: background,
    backgroundImage: backgroundImage,
    topText: topText,
    mainText: mainText,
    mainTextStyles: mainTextStyles || {
      fontSize: mainTextFontSize,
      fontWeight: mainTextFontWeight,
      lineHeight: mainTextLineHeight,
    },
    listButtons: listButtons,
  }

  // Merge button styles
  const finalButtonStyles: HeroBannerButtonStyles = {
    size: buttonStyles?.size || buttonSize,
    fontSize: buttonStyles?.fontSize || buttonFontSize,
    fontWeight: buttonStyles?.fontWeight || buttonFontWeight,
    lineHeight: buttonStyles?.lineHeight || buttonLineHeight,
    gap: buttonStyles?.gap || buttonGap,
  }

  return (
    <div id={id} className={twMerge("relative w-full", className)}>
      <HeroBannerSlideContent slide={singleSlide} buttonStyles={finalButtonStyles} />
    </div>
  )
}
